import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  TextField,
  IconButton,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import GridClearIcon from "@mui/icons-material/Clear";
import { useNavigate, useParams } from "react-router-dom";
import { MAIN_URL } from "../../../Configurations/Urls";

import {OTHER_LIVE_URL} from "../../../Configurations/Urls"
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import toast from "react-hot-toast";
import Header from "../../DataLayouts/Header";
import { formatDate } from "../../../Utils/DateAndTime";

function ProjectTaskScheduleForm({ mode: modeProp }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const mode = modeProp ?? (id ? "edit" : "add");

  const { userData, reporting_manager_employees } = useAuthStore();
  const org = userData?.organization;
  const loginUserOrgPersonId = userData?.loginuser?.[0]?.organization_person_id;

  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [organizationPersonDisplayName, setOrganizationPersonDisplayName] = useState("");

  const [formData, setFormData] = useState({
    organization_id: org?.organization_id || "",
    organization_project_task_id: "",
    scheduled_date: "",
    scheduled_time: "",
    organization_person_id: "",
    allocated_minutes: "",
    is_reassigned: false,
    task_status: "",
    status_remarks: "",
  });

  const [selectOptions, setSelectOptions] = useState({
    Tasks: [],
    Employees: [],
  });

  // Fetch Existing Data (Edit Mode)
  const fetchScheduleData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${MAIN_URL}/api/project-management/project-task-schedule/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = res.data?.data;
      if (data) {
        const orgPerson = data.organization_person || data.organizationPerson;
        const person = orgPerson?.person || orgPerson?.Person;
        const personCode = orgPerson?.person_code;
        const displayName = person
          ? `${person.first_name || ""} ${person.last_name || ""}${personCode ? ` : ${personCode}` : ""}`.trim()
          : "";
        setOrganizationPersonDisplayName(displayName);
        setFormData({
          organization_id: data.organization_id || "",
          organization_project_task_id: data.organization_project_task_id || "",
          scheduled_date: formatDate(data.scheduled_date) || "",
          scheduled_time: data.scheduled_time || "",
          organization_person_id: data.organization_person_id || "",
          allocated_minutes: data.allocated_minutes || "",
          is_reassigned: data.is_reassigned ? true : false,
          task_status: data.task_status || "",
          status_remarks: data.status_remarks || "",
        });
      }
    } catch (error) {
      toast.error("Failed to load schedule.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((mode === "edit" || mode === "view") && id) fetchScheduleData();
  }, [id, mode]);

  // Fetch Tasks
  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${MAIN_URL}/api/project-management/project-tasks`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          per_page: "all",
          organization_id: org?.organization_id,
        },
      });

      
      const sortedTasks = [...res.data.data].sort((a, b) =>
        (a.task_title || "").localeCompare(b.task_title || "")
      );

      setSelectOptions((prev) => ({ ...prev, Tasks: sortedTasks }));
    } catch (err) {
      console.log("Failed to load tasks");
    }
  };

  // Fetch Employees
  const fetchEmployeesOfOrg = async () => {
    try {
      //const res = await axios.get(`${MAIN_URL}/api/organizations/employees?type=Intern`, {
      const res = await axios.get(`${OTHER_LIVE_URL}/api/organizations/${org.organization_id}/persons-data-all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
       
      });



        // Filter only employees (person_type_id = 1) - show all org employees for reporting managers
        let EmployeeOnly = (res.data.persons || []).filter(
          (person) => person.organization_person?.person_type_id === 1
        );
        const sortedEmps = [...EmployeeOnly].sort((a, b) => {
          const aName = `${a.first_name || ''} ${a.last_name || ''}`.trim();
          const aCode = a.organization_person?.person_code || '';
          const bName = `${b.first_name || ''} ${b.last_name || ''}`.trim();
          const bCode = b.organization_person?.person_code || '';
          return `${aName} - ${aCode}`.localeCompare(`${bName} - ${bCode}`);
        });

      setSelectOptions((prev) => ({ ...prev, Employees: sortedEmps }));
    } catch (err) {
      console.log("Failed to load employees");
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchEmployeesOfOrg();
  }, [org?.organization_id]);

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!formData.organization_project_task_id)
      errors.organization_project_task_id = "Task is required";

    if (!formData.allocated_minutes || formData.allocated_minutes <= 0)
      errors.allocated_minutes = "Allocated minutes must be greater than 0";

    if (!formData.task_status)
      errors.task_status = "Task status is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit Form
  const handleSubmit = async () => {
    setFormErrors({});
    if (!validateForm()) {
      toast.error("Fix validation errors");
      return;
    }

    setBtnLoading(true);
    try {
      const token = localStorage.getItem("token");

      if (mode === "edit") {
        await axios.put(
          `${MAIN_URL}/api/project-management/project-task-schedule/${id}`,
           { ...formData, organization_id: org?.organization_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Schedule updated");
      } else {
        await axios.post(
          `${MAIN_URL}/api/project-management/project-task-schedule`,
           { ...formData, organization_id: org?.organization_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Schedule added");
      }

      navigate("/project-task-schedule");
    } catch (err) {
      if (err.response?.status === 422) {
        toast.error("Validation failed");
        setFormErrors(err.response.data.errors);
      } else {
        toast.error("Operation failed");
      }
    }
    setBtnLoading(false);
  };

  // Handle Inputs
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "is_reassigned") {
      setFormData((prev) => ({ ...prev, is_reassigned: !prev.is_reassigned }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const viewModeEmployeeName = React.useMemo(() => {
    if (organizationPersonDisplayName) return organizationPersonDisplayName;
    const idVal = formData.organization_person_id;
    if (!idVal) return "—";
    const emp = selectOptions?.Employees?.find(
      (e) => (e.organization_person_id || e.organization_person?.organization_person_id) == idVal
    );
    if (!emp) return "—";
    const firstName = emp.first_name || emp.organization_person?.person?.first_name || emp.person?.first_name || "";
    const lastName = emp.last_name || emp.organization_person?.person?.last_name || emp.person?.last_name || "";
    const personCode = emp.organization_person?.person_code || "";
    return `${firstName} ${lastName}`.trim() + (personCode ? ` : ${personCode}` : "") || "—";
  }, [organizationPersonDisplayName, formData.organization_person_id, selectOptions?.Employees]);

  const filterTasks = (options, { inputValue }) => {
    const query = (inputValue || "").trim().toLowerCase();
    if (query.length < 2) return [];

    const filtered = [];
    for (const task of options) {
      const label = (task.task_title || "").toLowerCase().trim();
      if (label.includes(query)) {
        filtered.push(task);
      }
      if (filtered.length >= 50) break;
    }
    return filtered;
  };

  const filterEmployees = (options, { inputValue }) => {
    const query = (inputValue || "").trim().toLowerCase();
    if (query.length < 2) return [];

    const filtered = [];
    for (const emp of options) {
      const firstName =
        emp.first_name ||
        emp.organization_person?.person?.first_name ||
        emp.person?.first_name ||
        "";
      const lastName =
        emp.last_name ||
        emp.organization_person?.person?.last_name ||
        emp.person?.last_name ||
        "";
      const personCode = emp.organization_person?.person_code || "";
      const label = `${firstName} ${lastName}`.trim() + (personCode ? ` : ${personCode}` : "");

      if (label.toLowerCase().includes(query)) {
        filtered.push(emp);
      }
      if (filtered.length >= 50) break;
    }
    return filtered;
  };

  return (
    <Box px={4} py={4}>
      <Header
        mode={mode}
        updateMessage={"Employee Task Schedules"}
        addMessage={"Employee Task Schedules"}
        homeLink={"/project-task-schedule"}
        homeText={"Employee Task Schedule"}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper elevation={4} sx={{ p: 3 }}>
              <Grid container spacing={2}>

                <Autocomplete
                  fullWidth
                  disabled={mode === "view"}
                  filterOptions={filterTasks}
                  options={selectOptions?.Tasks || []}
                  getOptionLabel={(option) => option.task_title || ""}
                  value={
                    selectOptions?.Tasks?.find(
                      (t) =>
                        t.organization_project_task_id ===
                        formData.organization_project_task_id
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleChange({
                      target: {
                        name: "organization_project_task_id",
                        value: newValue
                          ? newValue.organization_project_task_id
                          : "",
                      },
                    });
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.organization_project_task_id ===
                    value.organization_project_task_id
                  }
                  clearOnEscape
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Task"
                      fullWidth
                      error={!!formErrors.organization_project_task_id}
                      helperText={formErrors.organization_project_task_id}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {formData.organization_project_task_id &&
                              mode !== "view" && (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleChange({
                                        target: {
                                          name: "organization_project_task_id",
                                          value: "",
                                        },
                                      })
                                    }
                                  >
                                    <GridClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {mode === "view" ? (
                  <TextField
                    fullWidth
                    disabled
                    label="Employee"
                    value={viewModeEmployeeName}
                  />
                ) : (
                  <Autocomplete
                  fullWidth
                  disabled={mode === "view"}
                  filterOptions={filterEmployees}
                  options={selectOptions?.Employees || []}
                    getOptionLabel={(option) => {
                      const firstName =
                        option.first_name ||
                        option.organization_person?.person?.first_name ||
                        option.person?.first_name ||
                        "";
                      const lastName =
                        option.last_name ||
                        option.organization_person?.person?.last_name ||
                        option.person?.last_name ||
                        "";
                      const personCode =
                        option.organization_person?.person_code || "";
                      const displayName = `${firstName} ${lastName}`.trim();
                      return personCode
                        ? `${displayName} : ${personCode}`
                        : displayName || "Unknown Employee";
                    }}
                    value={
                      selectOptions?.Employees?.find((e) => {
                        const personId =
                          e.organization_person_id ||
                          e.organization_person?.organization_person_id;
                        return (
                          personId ==
                          (formData.organization_person_id ?? "")
                        );
                      }) || null
                    }
                    onChange={(event, newValue) => {
                      const personId =
                        newValue?.organization_person_id ||
                        newValue?.organization_person
                          ?.organization_person_id ||
                        "";
                      handleChange({
                        target: {
                          name: "organization_person_id",
                          value: personId,
                        },
                      });
                    }}
                    isOptionEqualToValue={(option, value) => {
                      const optId =
                        option.organization_person_id ||
                        option.organization_person?.organization_person_id;
                      const valId =
                        value.organization_person_id ||
                        value.organization_person?.organization_person_id;
                      return optId === valId;
                    }}
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employee"
                        error={!!formErrors.organization_person_id}
                        helperText={formErrors.organization_person_id}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {formData.organization_person_id &&
                                mode !== "view" && (
                                  <InputAdornment position="end">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleChange({
                                          target: {
                                            name: "organization_person_id",
                                            value: "",
                                          },
                                        })
                                      }
                                    >
                                      <GridClearIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}

                <TextField
                  fullWidth
                  disabled={mode === "view"}
                  type="date"
                  label="Schedule Date"
                  name="scheduled_date"
                  value={formData.scheduled_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!formErrors.scheduled_date}
                  helperText={formErrors.scheduled_date}
                />

                <TextField
                  fullWidth
                  disabled={mode === "view"}
                  type="time"
                  label="Schedule Time"
                  name="scheduled_time"
                  value={formData.scheduled_time}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!formErrors.scheduled_time}
                  helperText={formErrors.scheduled_time}
                />

                <TextField
                  fullWidth
                  disabled={mode === "view"}
                  type="number"
                  label="Allocated Minutes"
                  name="allocated_minutes"
                  value={formData.allocated_minutes}
                  onChange={handleChange}
                  error={!!formErrors.allocated_minutes}
                  helperText={formErrors.allocated_minutes}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_reassigned}
                      name="is_reassigned"
                      onChange={handleChange}
                      disabled={mode === "view"}
                    />
                  }
                  label="Is Reassigned"
                />

                <TextField
                  select
                  fullWidth
                  disabled={mode === "view"}
                  label="Task Status"
                  name="task_status"
                  value={formData.task_status}
                  onChange={handleChange}
                  error={!!formErrors.task_status}
                  helperText={formErrors.task_status}
                >
                  {[
                    "Pending",
                    "In Progress",
                    "In Review",
                    "Approved",
                    "Rejected",
                    "Completed",
                    "Blocked",
                    "Paused",
                  ].map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  disabled={mode === "view"}
                  multiline
                  minRows={3}
                  label="Status Remarks"
                  name="status_remarks"
                  value={formData.status_remarks}
                  onChange={handleChange}
                />

              </Grid>

              {/* Submit Button */}
              {mode !== "view" && (
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleSubmit}
                disabled={loading || btnLoading}
                sx={{
                  borderRadius: 2,
                  minWidth: 120,
                  mt: 2,
                }}
              >
                {btnLoading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : mode === "edit" ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </Button>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default ProjectTaskScheduleForm;
